import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../../app/store.js';
import { geocodeApi, type GeocodeMatch } from '../../services/geocodeApi.js';
import { extentRequested, flyToRequested } from '../slices/mapSlice.js';

/**
 * Resolve a place query and move the map to the best match.
 *
 * Prefers framing an extent over centering on a point: a county or quad result
 * is far more useful shown at its own scale than centered at an arbitrary zoom.
 * Point results (addresses) fall back to a fly-to at street level.
 */
export const searchAndZoom = createAsyncThunk<
  GeocodeMatch | null,
  string,
  { state: RootState; dispatch: AppDispatch }
>('search/searchAndZoom', async (text, thunk) => {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const matches = await thunk
    .dispatch(geocodeApi.endpoints.geocode.initiate(trimmed))
    .unwrap();

  const best = matches[0];
  if (!best) return null;

  if (best.bbox) {
    thunk.dispatch(extentRequested({ bbox: best.bbox }));
  } else {
    thunk.dispatch(flyToRequested({ center: best.center, zoom: 15 }));
  }

  return best;
});
